import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "prp_cart_v2";
const PROMO_KEY = "prp_promo_v1";

// Pick the effective unit price right now (sale if active, else RRP).
// Mirrors backend discount_service.effective_unit_price.
const isWithinWindow = (start, end) => {
    const now = Date.now();
    if (start && new Date(start).getTime() > now) return false;
    if (end && new Date(end).getTime() <= now) return false;
    return true;
};

export const effectivePrice = (p) => {
    const rrp = Number(p?.price || 0);
    const sale = Number(p?.sale_price || 0);
    if (sale > 0 && sale < rrp && isWithinWindow(p?.sale_starts_at, p?.sale_ends_at)) return sale;
    return rrp;
};

export const isOnSale = (p) => {
    const rrp = Number(p?.price || 0);
    const sale = Number(p?.sale_price || 0);
    return sale > 0 && sale < rrp && isWithinWindow(p?.sale_starts_at, p?.sale_ends_at);
};

/** Canonical rule: a product is a pre-order iff its persisted `pre_order.enabled`
 * flag is true. Anywhere on the frontend that needs to render a Pre-Order state
 * (badge, button copy, cart line, PDP) should call this helper — never reach
 * into `product.availability` (deprecated) or the raw pre_order object. */
export const isPreOrder = (p) => !!(p && p.pre_order && p.pre_order.enabled);

/** Remaining pre-order allocation. Returns Infinity when max_quantity is null
 * (admin flagged the pre-order as unlimited). Returns 0 for non-pre-order or
 * sold-out pre-orders. */
export const preOrderRemaining = (p) => {
    if (!isPreOrder(p)) return 0;
    const po = p.pre_order || {};
    if (po.max_quantity === null || po.max_quantity === undefined) return Infinity;
    return Math.max(0, Number(po.max_quantity || 0) - Number(po.current_reservations || 0));
};

export const isPreOrderSoldOut = (p) => isPreOrder(p) && preOrderRemaining(p) <= 0;

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [promoCode, setPromoCode] = useState(() => localStorage.getItem(PROMO_KEY) || null);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        if (promoCode) localStorage.setItem(PROMO_KEY, promoCode);
        else localStorage.removeItem(PROMO_KEY);
    }, [promoCode]);

    const addItem = (product, quantity = 1) => {
        // Defense-in-depth stock guard — never let an out-of-stock or inactive product
        // enter the cart. Pre-order items are allowed even when stock == 0, as long
        // as the pre-order allocation cap hasn't been reached.
        const preOrder = isPreOrder(product);
        if (!product || product.active === false) {
            return { ok: false, reason: "inactive" };
        }
        if (preOrder && preOrderRemaining(product) <= 0) {
            return { ok: false, reason: "preorder_sold_out" };
        }
        if (!preOrder && Number(product.stock ?? 0) <= 0) {
            return { ok: false, reason: "out_of_stock" };
        }
        setItems((prev) => {
            const existing = prev.find((i) => i.slug === product.slug);
            // Pre-orders may have a max_quantity cap; otherwise fall back to stock/20
            const preRemaining = preOrder ? preOrderRemaining(product) : null;
            const preMax = preOrder && Number.isFinite(preRemaining) ? Math.max(1, preRemaining) : null;
            const maxAllowed = Math.min(
                20,
                preMax !== null ? preMax : Number(product.stock ?? 20)
            );
            if (existing) {
                return prev.map((i) =>
                    i.slug === product.slug ? { ...i, quantity: Math.min(maxAllowed, i.quantity + quantity) } : i
                );
            }
            return [
                ...prev,
                {
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    sale_price: product.sale_price || null,
                    sale_starts_at: product.sale_starts_at || null,
                    sale_ends_at: product.sale_ends_at || null,
                    image: product.image,
                    dosage: product.dosage,
                    stock: product.stock,
                    pre_order: product.pre_order || null,
                    quantity: Math.min(maxAllowed, quantity),
                },
            ];
        });
        return { ok: true };
    };

    const removeItem = (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug));

    const updateQuantity = (slug, quantity) => {
        if (quantity <= 0) return removeItem(slug);
        setItems((prev) =>
            prev.map((i) => (i.slug === slug ? { ...i, quantity: Math.min(20, quantity) } : i))
        );
    };

    const clear = () => { setItems([]); setPromoCode(null); };

    // Subtotal at RRP (always)
    const subtotalRrp = items.reduce((sum, i) => sum + Number(i.price || 0) * i.quantity, 0);
    // Subtotal with sale prices baked in
    const subtotalSale = items.reduce((sum, i) => sum + effectivePrice(i) * i.quantity, 0);
    // Total alias (backwards-compatible with rest of the codebase)
    const total = subtotalSale;
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const savingsFromSale = Math.max(0, subtotalRrp - subtotalSale);

    return (
        <CartContext.Provider
            value={{
                items,
                addItem,
                removeItem,
                updateQuantity,
                clear,
                total,
                subtotalRrp,
                subtotalSale,
                savingsFromSale,
                count,
                promoCode,
                setPromoCode,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};
